import { F } from "../../styles/palette";

// Convierte un valor Firestore Timestamp o Date a ms epoch
function toMs(v) {
  if (!v) return 0;
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  return new Date(v).getTime();
}

const PAD = { top: 18, right: 16, bottom: 44, left: 48 };
const VW = 400, VH = 190;
const PW = VW - PAD.left - PAD.right;
const PH = VH - PAD.top - PAD.bottom;

/**
 * Gráfica de línea SVG reutilizable.
 * Props:
 *   data      [{x: Timestamp|Date|number, y: number}]
 *   label     string — título
 *   color     string — color de línea/puntos
 *   unit      string — unidad del eje Y (ej: "%", "pts")
 *   yMin      number — mínimo del eje Y (default: automático)
 *   yMax      number — máximo del eje Y (default: automático)
 *   refLine   number — valor de línea de referencia horizontal (opcional)
 *   refColor  string — color de la línea de referencia (default: "#ef4444")
 *   refLabel  string — etiqueta de la línea de referencia
 */
export default function LineChart({ data = [], label, color = "#2dd4bf", unit = "", yMin, yMax, refLine, refColor = "#ef4444", refLabel }) {
  if (!data || data.length === 0) {
    return (
      <div style={{ padding: "16px 0", textAlign: "center", color: "#64748b", fontSize: 12, fontFamily: F.b }}>
        Sin datos suficientes para graficar
      </div>
    );
  }

  if (data.length === 1) {
    // Con un solo punto, mostrar solo el valor sin línea
    const pt = data[0];
    const d = toMs(pt.x) > 0 ? new Date(toMs(pt.x)) : null;
    const dateStr = d ? d.toLocaleDateString("es-CO", { month: "short", day: "numeric" }) : "";
    return (
      <div style={{ padding: "10px 0 4px", textAlign: "center" }}>
        <div style={{ fontSize: 24, color, fontFamily: F.m, fontWeight: 700 }}>{pt.y}{unit}</div>
        <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.b }}>{dateStr}</div>
        <div style={{ fontSize: 10, color: "#475569", marginTop: 4, fontFamily: F.b }}>Se necesitan ≥2 sesiones para graficar tendencia</div>
      </div>
    );
  }

  // Calcular rangos
  const xs = data.map(d => toMs(d.x));
  const ys = data.map(d => d.y);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const rawYMin = yMin !== undefined ? yMin : Math.min(...ys);
  const rawYMax = yMax !== undefined ? yMax : Math.max(...ys);
  const yRange = rawYMax - rawYMin || 1;
  const xRange = xMax - xMin || 1;

  // Normalizar a coordenadas SVG
  const nx = x => PAD.left + ((x - xMin) / xRange) * PW;
  const ny = y => PAD.top + PH - ((y - rawYMin) / yRange) * PH;

  // Polyline points
  const points = data.map(d => `${nx(toMs(d.x))},${ny(d.y)}`).join(" ");

  // Líneas de referencia del eje Y (3 líneas horizontales)
  const yTicks = [rawYMin, rawYMin + yRange / 2, rawYMax];

  // Etiquetas eje X (máx 5, espaciadas)
  const xTickCount = Math.min(data.length, 5);
  const xTickIndices = data.length <= 5
    ? data.map((_, i) => i)
    : Array.from({ length: xTickCount }, (_, i) => Math.round((i / (xTickCount - 1)) * (data.length - 1)));

  return (
    <div>
      {label && (
        <div style={{ fontSize: 12, color: "#94a3b8", fontFamily: F.b, marginBottom: 4 }}>{label}</div>
      )}
      <svg viewBox={`0 0 ${VW} ${VH}`} style={{ width: "100%", overflow: "visible" }}>
        {/* Líneas de cuadrícula horizontales */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={PAD.left} y1={ny(tick)}
              x2={PAD.left + PW} y2={ny(tick)}
              stroke="rgba(100,116,139,0.15)" strokeWidth="1"
            />
            <text
              x={PAD.left - 6} y={ny(tick) + 4}
              textAnchor="end" fontSize="10" fill="#64748b" fontFamily="monospace">
              {Number.isInteger(tick) ? tick : tick.toFixed(1)}{unit}
            </text>
          </g>
        ))}

        {/* Línea de referencia clínica */}
        {refLine !== undefined && refLine >= rawYMin && refLine <= rawYMax && (
          <g>
            <line
              x1={PAD.left} y1={ny(refLine)}
              x2={PAD.left + PW} y2={ny(refLine)}
              stroke={refColor} strokeWidth="1.5" strokeDasharray="5,4"
            />
            {refLabel && (
              <text
                x={PAD.left + PW + 3} y={ny(refLine) + 4}
                fontSize="9" fill={refColor} fontFamily="monospace">
                {refLabel}
              </text>
            )}
          </g>
        )}

        {/* Línea del gráfico */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Área bajo la curva (sutil) */}
        <polygon
          points={`${nx(xs[0])},${PAD.top + PH} ${points} ${nx(xs[xs.length - 1])},${PAD.top + PH}`}
          fill={color}
          opacity="0.08"
        />

        {/* Puntos */}
        {data.map((d, i) => {
          const cx = nx(toMs(d.x));
          const cy = ny(d.y);
          const dateObj = toMs(d.x) > 0 ? new Date(toMs(d.x)) : null;
          const dateStr = dateObj ? dateObj.toLocaleDateString("es-CO", { month: "short", day: "numeric", year: "numeric" }) : "";
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r="5" fill={color} opacity="0.9" />
              <circle cx={cx} cy={cy} r="3" fill="#0f172a" />
              <title>{dateStr}: {d.y}{unit}</title>
            </g>
          );
        })}

        {/* Eje X — etiquetas de fecha */}
        {xTickIndices.map(i => {
          const d = data[i];
          const dateObj = toMs(d.x) > 0 ? new Date(toMs(d.x)) : null;
          const label = dateObj ? dateObj.toLocaleDateString("es-CO", { month: "short", day: "numeric" }) : "";
          return (
            <text key={i}
              x={nx(toMs(d.x))} y={PAD.top + PH + 16}
              textAnchor="middle" fontSize="10" fill="#64748b" fontFamily="monospace">
              {label}
            </text>
          );
        })}

        {/* Ejes */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + PH} stroke="rgba(100,116,139,0.25)" strokeWidth="1" />
        <line x1={PAD.left} y1={PAD.top + PH} x2={PAD.left + PW} y2={PAD.top + PH} stroke="rgba(100,116,139,0.25)" strokeWidth="1" />
      </svg>
    </div>
  );
}
